import { useChangePassword, useUpdateUserInfo, useUserInfo } from "@/api/user";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToggleTheme } from "@/hooks/useToggleTheme";
import * as analytics from "@/utils/analytics";
import { useThemeContext } from "@cohesive/ui-components";
import { zodResolver } from "@hookform/resolvers/zod";
import { Moon, Palette, Shield, Sun, User } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import * as z from "zod";

const US_TIMEZONES: { [key: string]: string } = {
  "America/New_York": "Eastern Time (UTC-05:00)",
  "America/Chicago": "Central Time (UTC-06:00)",
  "America/Denver": "Mountain Time (UTC-07:00)",
  "America/Los_Angeles": "Pacific Time (UTC-08:00)",
  "America/Anchorage": "Alaska Time (UTC-09:00)",
  "Pacific/Honolulu": "Hawaii-Aleutian Time (UTC-10:00)",
};

const formSchema = z.object({
  first_name: z.string().optional(),
  last_name: z.string().optional(),
  jobTitle: z.string().optional(),
  department: z.string().optional(),
  specialInstructions: z.string().optional(),
  timeZone: z.string().optional(),
});

// Theme toggle component for settings
const ThemeToggleSetting: React.FC = () => {
  const { mode } = useThemeContext();
  const { toggleTheme } = useToggleTheme();
  return (
    <Button
      type="button"
      variant="outline"
      className="max-w-md flex items-center gap-2"
      onClick={toggleTheme}
      aria-label={`Switch to ${mode === "light" ? "dark" : "light"} theme`}
    >
      {mode === "light" ? <Moon /> : <Sun />}
      {`Switch to ${mode === "light" ? "dark" : "light"} theme`}
    </Button>
  );
};

const MySettings: React.FC = () => {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: "all",
    defaultValues: {
      first_name: "",
      last_name: "",
      jobTitle: "",
      department: "",
      specialInstructions: "",
      timeZone: "",
    },
  });

  const { data: userInfo } = useUserInfo();
  const updateUserInfo = useUpdateUserInfo();
  const changePassword = useChangePassword();

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  useEffect(() => {
    if (userInfo) {
      form.reset({
        first_name: userInfo.user.first_name,
        last_name: userInfo.user.last_name,
        jobTitle: userInfo.organization_user.metadata.job_title || "",
        department: userInfo.organization_user.metadata.department || "",
        specialInstructions:
          userInfo.organization_user.metadata.special_instructions || "",
        timeZone: userInfo.organization_user.metadata.time_zone || "",
      });
    }
  }, [userInfo]);

  form.watch();

  // Replace handleSubmit with onSubmit
  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      const updatedData = {
        user: {
          first_name: values.first_name,
          last_name: values.last_name,
        },
        organization_user: {
          metadata: {
            job_title: values.jobTitle,
            department: values.department,
            special_instructions: values.specialInstructions,
            time_zone: values.timeZone,
          },
        },
      };

      await updateUserInfo.mutateAsync(updatedData);

      analytics.trackUserProfileEdited({
        jobTitle: values.jobTitle,
        department: values.department,
        specialInstructions: values.specialInstructions,
        timeZone: values.timeZone,
      });

      toast.success("Your settings have been successfully updated.");
    } catch (error) {
      toast.error("Failed to update settings. Please try again.");
    }
  };

  const handleChangePassword = () => {
    setIsDialogOpen(true);
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    changePassword
      .mutateAsync()
      .then(() => {
        setIsDialogOpen(false);
        toast.success(
          "Please check your email for instructions to reset your password.",
        );
      })
      .catch((e) => {
        toast.error(e.message);
      });
  };

  return (
    <>
      <title>Account Settings</title>
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <div className="flex items-center space-x-2">
              <User className="h-5 w-5" />
              <CardTitle>My Info</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-4"
              >
                <FormField
                  control={form.control}
                  name="first_name"
                  render={({ field }) => (
                    <FormItem className="max-w-md">
                      <FormLabel>First Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Your first name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="last_name"
                  render={({ field }) => (
                    <FormItem className="max-w-md">
                      <FormLabel>Last Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Your last name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="jobTitle"
                  render={({ field }) => (
                    <FormItem className="max-w-md">
                      <FormLabel>Job Title</FormLabel>
                      <FormControl>
                        <Input placeholder="Your job title" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="timeZone"
                  render={({ field }) => (
                    <FormItem className="max-w-md">
                      <FormLabel>Timezone</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select your time zone" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {Object.entries(US_TIMEZONES).map(
                            ([value, label]) => (
                              <SelectItem key={value} value={value}>
                                {label}
                              </SelectItem>
                            ),
                          )}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button
                  className="mt-8!"
                  type="submit"
                  disabled={
                    !form.formState.isDirty ||
                    !form.formState.isValid ||
                    updateUserInfo.isPending
                  }
                  isLoading={updateUserInfo.isPending}
                >
                  Save Changes
                </Button>
              </form>
            </Form>
          </CardContent>
        </Card>
        {/* Appearance Section */}
        <Card>
          <CardHeader>
            <div className="flex items-center space-x-2">
              <Palette className="h-5 w-5" />
              <CardTitle>Appearance</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="@container">
            <div className="flex flex-col gap-6 @md:flex-row @md:items-center @md:justify-between">
              <div className="space-y-1">
                <h3 className="font-medium">Theme</h3>
                <p className="text-sm text-muted-foreground">
                  Change the theme of the app.
                </p>
              </div>
              <ThemeToggleSetting />
            </div>
          </CardContent>
        </Card>

        {userInfo?.user?.provider === "auth0" && (
          <>
            <Card>
              <CardHeader>
                <div className="flex items-center space-x-2">
                  <Shield className="h-5 w-5" />
                  <CardTitle>Account Security</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="@container">
                <div className="flex flex-col gap-6 @md:flex-row @md:items-center @md:justify-between">
                  <div className="space-y-1">
                    <h3 className="font-medium">Password</h3>
                    <p className="text-sm text-muted-foreground">
                      Change your password to login to your account.
                    </p>
                  </div>
                  <Button variant="outline" onClick={handleChangePassword}>
                    Change password
                  </Button>
                </div>
              </CardContent>
            </Card>
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Reset password</DialogTitle>
                  <DialogDescription>
                    We will send you an email with instructions to reset your
                    password.
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handlePasswordSubmit} className="space-y-4">
                  <Button
                    disabled={changePassword.isPending}
                    isLoading={changePassword.isPending}
                    type="submit"
                  >
                    Send Reset Email
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </>
        )}
      </div>
    </>
  );
};

export default MySettings;
